import { useMemo, useState } from 'react';
import { Download, KeyRound, Loader2, PackageOpen } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/components/ui/use-toast';
import { useMarketplaceKeys } from '@/hooks/supabase/useMarketplaceKeys';
import type { MapleMarketApp } from '@/data/mapleMarketApps';

interface KeysPanelProps {
  product?: MapleMarketApp;
  products?: MapleMarketApp[];
  onOpenProduct?: (productId: string, initialTab?: 'keys') => void;
}

function downloadTxtFile(fileName: string, values: string[]) {
  const blob = new Blob([values.join('\n')], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
}

export default function KeysPanel({ product, products = [], onOpenProduct }: KeysPanelProps) {
  const { toast } = useToast();
  const [quantity, setQuantity] = useState('25');
  const { batches, recentKeys, summary, loading, generating, error, generateBatch } = useMarketplaceKeys(product?.id ?? null, Boolean(product));

  const batchOptions = useMemo(() => products.slice().sort((left, right) => left.name.localeCompare(right.name)), [products]);

  if (!product) {
    return (
      <div className="space-y-6 p-6 lg:p-8">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Maple Keys</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Choose a product to generate Maple Keys, inspect active inventory, and review redemption history.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Select a product</CardTitle>
            <CardDescription>Maple Keys are managed per product.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {batchOptions.length === 0 && (
              <div className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">
                No accessible products are available for key management.
              </div>
            )}
            {batchOptions.map((entry) => (
              <div key={entry.id} className="flex items-center gap-4 rounded-xl border border-border p-3">
                <img src={entry.image} alt={entry.name} className="h-14 w-14 rounded-lg object-cover" />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-foreground">{entry.name}</div>
                  <div className="truncate text-sm text-muted-foreground">{entry.sku}</div>
                </div>
                <Button type="button" variant="outline" onClick={() => onOpenProduct?.(entry.id, 'keys')}>
                  Manage keys
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleGenerateBatch = async () => {
    const nextQuantity = Number(quantity);
    if (!Number.isInteger(nextQuantity) || nextQuantity <= 0 || nextQuantity > 5000) {
      toast({
        title: 'Invalid quantity',
        description: 'Enter a whole number between 1 and 5000.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const batch = await generateBatch(nextQuantity);
      downloadTxtFile(`${product.sku}-maple-keys-${batch.batchId}.txt`, batch.keys);
      toast({
        title: 'Maple Keys generated',
        description: `${batch.keys.length} new keys were created and downloaded as TXT.`,
      });
    } catch (generationError) {
      toast({
        title: 'Maple Key generation failed',
        description: generationError instanceof Error ? generationError.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Maple Keys</h3>
          <p className="text-sm text-muted-foreground">
            Generate single-use UUID keys, download them as TXT, and review redemption activity for {product.name}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={1}
            max={5000}
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
            className="w-28"
          />
          <Button type="button" onClick={() => void handleGenerateBatch()} disabled={generating}>
            {generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}
            Generate batch
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total generated</CardDescription>
            <CardTitle className="mt-2 text-3xl">{summary.totalGenerated}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Active</CardDescription>
            <CardTitle className="mt-2 text-3xl">{summary.activeCount}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Redeemed</CardDescription>
            <CardTitle className="mt-2 text-3xl">{summary.redeemedCount}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Revoked</CardDescription>
            <CardTitle className="mt-2 text-3xl">{summary.revokedCount}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent key batches</CardTitle>
          <CardDescription>Each batch can be downloaded as a TXT file with one UUID per line.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-hidden rounded-xl border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Batch</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Generated</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead>Redeemed</TableHead>
                  <TableHead className="text-right">Download</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {batches.map((batch) => (
                  <TableRow key={batch.id}>
                    <TableCell className="font-medium">{batch.id.slice(0, 8)}</TableCell>
                    <TableCell>{formatDate(batch.createdAt)}</TableCell>
                    <TableCell>{batch.quantity}</TableCell>
                    <TableCell>{batch.activeCount}</TableCell>
                    <TableCell>{batch.redeemedCount}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => downloadTxtFile(`${product.sku}-maple-keys-${batch.id}.txt`, batch.keys.map((key) => key.keyValue))}
                        disabled={batch.keys.length === 0}
                      >
                        <Download className="mr-2 h-4 w-4" />
                        TXT
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && batches.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="h-24 text-center text-sm text-muted-foreground">
                      No Maple Key batches have been created for this product yet.
                    </TableCell>
                  </TableRow>
                )}
                {loading && (
                  <TableRow>
                    <TableCell colSpan={6} className="h-24 text-center text-sm text-muted-foreground">
                      Loading Maple Key batches…
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent keys</CardTitle>
          <CardDescription>Track active and redeemed Maple Keys at the individual key level.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-hidden rounded-xl border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Key</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Redeemed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentKeys.map((key) => (
                  <TableRow key={key.id}>
                    <TableCell className="font-mono text-xs">{key.keyValue}</TableCell>
                    <TableCell>
                      <Badge variant={key.status === 'redeemed' ? 'secondary' : 'outline'}>{key.status}</Badge>
                    </TableCell>
                    <TableCell>{formatDate(key.createdAt)}</TableCell>
                    <TableCell>{key.redeemedAt ? formatDate(key.redeemedAt) : 'Unused'}</TableCell>
                  </TableRow>
                ))}
                {!loading && recentKeys.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="h-24 text-center text-sm text-muted-foreground">
                      Generate a batch to start issuing Maple Keys.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>How Maple Keys work</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <div className="flex items-start gap-3 rounded-xl border border-border p-4">
            <PackageOpen className="mt-0.5 h-4 w-4 text-primary" />
            <p>Each generated Maple Key is a single UUID and can only be redeemed one time.</p>
          </div>
          <div className="flex items-start gap-3 rounded-xl border border-border p-4">
            <Download className="mt-0.5 h-4 w-4 text-primary" />
            <p>Downloaded TXT exports contain one Maple Key per line so they can be delivered in bulk.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
